import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import '../../../styles/Auth.css';

const Login = () => {
  const location = useLocation();
  const justVerified = location.state?.verified;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerificationHint, setShowVerificationHint] = useState(false);
  const resending = false;
  const [resendMessage, setResendMessage] = useState('');
  const [successBanner, setSuccessBanner] = useState(justVerified ? 'Email verified! You can now log in.' : '');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setShowVerificationHint(false);
    setResendMessage('');
    setSuccessBanner('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setShowVerificationHint(false);

    const result = await login(formData);
    
    if (result.success) {
      const { user_type } = result;
      if (user_type === 'admin') {
        navigate('/admin');
      } else {
        navigate('/internship/projects');
      }
    } else {
      setError(result.error);
      // Check if the error is about email verification
      if (result.error && result.error.toLowerCase().includes('verify your email')) {
        setShowVerificationHint(true);
      }
    }
    
    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;
    // Navigate to verify-email page with the email pre-filled
    navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <p>Login to continue your coding journey</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {successBanner && (
            <div className="success-message" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#166534', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#16a34a" opacity="0.15"/>
                <path d="M6 10l3 3 5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {successBanner}
            </div>
          )}
          {error && (
            <div className={`error-message ${showVerificationHint ? 'error-message-warning' : ''}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="currentColor"/>
              </svg>
              <div>
                <span>{error}</span>
                {showVerificationHint && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      className="resend-link-btn"
                      onClick={handleResendVerification}
                      disabled={resending}
                    >
                      {resending ? 'Sending...' : 'Verify your email →'}
                    </button>
                    {resendMessage && (
                      <span style={{ display: 'block', marginTop: '4px', color: '#38a169', fontSize: '13px' }}>
                        {resendMessage}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <div className="forgot-password-link">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register">Register here</Link>
          </p>
          <p style={{ marginTop: '12px' }}>
            <a href="/" className="back-to-landing">← Back to Home</a>
          </p>
          <p style={{ marginTop: '20px', fontSize: '12px', color: '#718096' }}>
            © 2026 BATOS IT SOLUTIONS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
